import classNames from "classnames";
import { Link } from "react-router-dom";
import { useGlobal } from "reactn";
import { Helmet } from "react-helmet-async";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getUrl } from "@outplayed/riot-assets";
import { MediaQuery } from "@outplayed/responsive";
import SearchBar from "components/SearchBar";
import { ContentSidePadding } from "components/ContentSidePadding";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { FreestarLeftRail, FreestarRightRail } from "components/Ads/FreestarRails";
import { getQueueTypeName, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { useGetFeaturedLiveGames, FeaturedGame } from "@ugg/shared/api/requests/featured-live-games";
import { getProfileOverviewUrl, getProfileLiveGameUrl } from "@ugg/shared/routes/app-routes";
import { getProBuildRankName, RankS } from "@ugg/shared/utils/rank-helpers";

export function LiveGameSplashContainer() {
  return (
    <div className="flex justify-center flex-1 mx-auto w-full max-md:w-full">
      <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail isClient>
        <div className="flex-1 min-w-0 mr-[-12px]">
          <FreestarLeftRail className="sticky top-[calc(var(--masthead-height)+100px)] transition-[top]" />
        </div>
      </MediaQuery>
      <ContentSidePadding className="mx-auto w-[1014px] max-md:w-full box-content">
        <LiveGameSplash />
      </ContentSidePadding>
      <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail isClient>
        <div className="flex-1 min-w-0 ml-[-12px]">
          <FreestarRightRail className="sticky top-[calc(var(--masthead-height)+100px)] transition-[top]" />
        </div>
      </MediaQuery>
    </div>
  );
}

function LiveGameSplash() {
  const [clientRegion] = useGlobal("clientRegion");

  const { data } = useGetFeaturedLiveGames(clientRegion, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    ssr: false,
  });

  const { getFeaturedGames } = data || {};
  const { gameList } = getFeaturedGames || {};

  return (
    <div className="flex flex-col items-center justify-center pb-[36px] mt-[110px] mx-auto">
      <Helmet>
        <title>U.GG - Featured Games</title>
      </Helmet>
      <div className="flex items-baseline">
        <img width={266} height={86} src={getUrl("ugg/logo/ugg-logo.svg")} />
        <div className="relative ml-[24px] text-[70px] font-semibold font-['Barlow']">
          <span className="text-accent-blue-400">/</span>
          <span>LIVE</span>
          <div className="absolute top-[8px] right-[-13px] w-[12px] h-[12px] rounded-[50%] bg-accent-orange-500"></div>
        </div>
      </div>
      <div className="large-search search-container mt-[60px] px-[24px] w-[700px] max-sm:w-full">
        <SearchBar
          placeholder={"Search Yourself or a Champion"}
          autoFocus={true}
          searchBarId="landing"
          goToLiveGame={true}
          region={clientRegion}
        />
      </div>
      <span className="mt-[12px] mb-[36px] text-[14px] font-medium text-lavender-50 text-center max-w-[369px]">
        You can also copy and paste the lobby discussion into the search field if the game has not started yet{" "}
      </span>
      <div className="flex flex-wrap justify-center gap-[12px] mt-[48px]">
        {gameList?.slice(0, 3).map((game, index) => (
          <div key={index} className="relative">
            {index === 0 && (
              <div className="absolute top-0 left-0 translate-y-[calc(-100%-24px)] w-full font-semibold text-[18px] leading-[22px]">
                Featured Games
              </div>
            )}
            <Game key={game.gameId} game={game} clientRegion={clientRegion} />
          </div>
        ))}
      </div>
    </div>
  );
}

const Player = (props: { player: FeaturedGame["participants"][number]; region: string; reverse: boolean }) => {
  const { player, region, reverse } = props;

  const { getChampionName, getChampionImg } = getRiotAssetsContext();

  const { riotUserName, riotTagLine, tier, rank } = player;
  const profileURLLive = getProfileOverviewUrl(region, riotUserName, riotTagLine);

  return (
    <div
      className={classNames("flex justify-end gap-[6px] text-right", {
        player__reverse: reverse,
        "flex-row-reverse !text-left": reverse,
      })}
    >
      <div className="flex flex-col">
        <Link className="max-w-[103px] text-[14px] font-medium text-lavender-50 truncate" to={profileURLLive}>
          {riotUserName}#{riotTagLine}
        </Link>
        <div className="text-[12px] text-lavender-200">
          {tier && rank ? getProBuildRankName(tier, rank) : getProBuildRankName(RankS.UNRANKED, "")}
        </div>
      </div>
      <div className="flex items-center justify-center w-[34px] h-[34px] overflow-hidden">
        <img
          className="w-full h-full scale-[1.1]"
          src={getChampionImg(player.championId)}
          alt={getChampionName(player.championId)}
        />
      </div>
    </div>
  );
};

const Game = (props: { game: FeaturedGame; clientRegion: string }) => {
  const { game, clientRegion } = props;

  let team1 =
    game.gameQueueConfigId === QueueTypeN.ARENA
      ? game.participants.slice(0, Math.floor(game.participants.length / 2))
      : game.participants.filter((player) => player.teamId === 100);
  let team2 =
    game.gameQueueConfigId === QueueTypeN.ARENA
      ? game.participants.slice(Math.floor(game.participants.length / 2), game.participants.length)
      : game.participants.filter((player) => player.teamId === 200);

  return (
    <div className="flex flex-col w-[330px] h-fit bg-purple-400 rounded-[3px]">
      <div className="p-[18px] border-b-[1px] border-b-purple-500">
        <HeaderBullet>
          <Link to={getProfileLiveGameUrl(clientRegion, game.participants[0].riotUserName, game.participants[0].riotTagLine)}>
            {getQueueTypeName(game.gameQueueConfigId)}
          </Link>
        </HeaderBullet>
      </div>
      <div className="flex gap-[8px] p-[18px]">
        {[team1, team2].map((team, teamIndex) => (
          <div key={teamIndex} className="flex flex-col gap-[18px]">
            {team.map((player) => (
              <Player
                key={player.riotUserName + player.riotTagLine}
                player={player}
                region={clientRegion}
                reverse={teamIndex === 0 ? false : true}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
